import { User, Truck, Users, Plus, Trash2, Minus, DollarSign, ImageOff, Loader2, FileCheck2, Pencil } from 'lucide-react'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import { Input } from '@/app/components/ui/input'
import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { CartItem, CartPrescription, DeliveryDetails, POINT_VALUE, PosCartData } from '@/app/services/pos.service'

export interface PosCartProps {
  saleType: 'regular' | 'delivery'
  setSaleType: (type: 'regular' | 'delivery') => void
  deliveryDetails: DeliveryDetails
  setShowDeliveryDetailsModal: (show: boolean) => void
  customerType: 'general' | 'registered'
  setCustomerType: (type: 'general' | 'registered') => void
  selectedCustomer: number | null
  setSelectedCustomer: (id: number) => void
  setShowNewCustomerModal: (show: boolean) => void
  customerPoints: number
  pointsEarned: number
  usePoints: boolean
  setUsePoints: (use: boolean) => void
  pointsToUse: number
  setPointsToUse: (points: number) => void
  totalBeforePoints: number
  cart: CartItem[]
  clearCart: () => void
  deleteSelectedItems: () => void
  toggleSelectItem: (id: number) => void
  removeFromCart: (id: number) => void
  updateQuantity: (id: number, delta: number) => void
  subtotal: number
  tax: number
  deliveryCost: number
  pointsDiscount: number
  total: number
  onProcessPayment: () => void
  isValidatingCart: boolean
  prescriptions?: CartPrescription[]
  regulatory?: PosCartData['regulatory']
  onEditPrescription?: () => void
}

export function PosCart({
  saleType,
  setSaleType,
  deliveryDetails,
  setShowDeliveryDetailsModal,
  customerType,
  setCustomerType,
  selectedCustomer,
  setSelectedCustomer,
  setShowNewCustomerModal,
  customerPoints,
  pointsEarned,
  usePoints,
  setUsePoints,
  pointsToUse,
  setPointsToUse,
  totalBeforePoints,
  cart,
  clearCart,
  deleteSelectedItems,
  toggleSelectItem,
  removeFromCart,
  updateQuantity,
  subtotal,
  tax,
  deliveryCost,
  pointsDiscount,
  total,
  onProcessPayment,
  isValidatingCart,
  prescriptions,
  regulatory,
  onEditPrescription,
}: PosCartProps) {
  return (
    <div className="flex h-full w-full flex-col border-l border-slate-200 bg-white lg:w-[450px]">
      <div className="space-y-3 border-b border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Carrito de Venta</h2>
          {cart.length > 0 && (
            <button
              onClick={clearCart}
              className="text-slate-400 hover:text-red-500 transition-colors"
              title="Vaciar carrito"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sale Type */}
        <div className="flex gap-2">
          <button
            onClick={() => setSaleType('regular')}
            className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${saleType === 'regular'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <User className="mx-auto mb-1 h-4 w-4" />
            <span className="text-xs font-medium">Venta en Tienda</span>
          </button>
          <button
            onClick={() => setSaleType('delivery')}
            className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${saleType === 'delivery'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <Truck className="mx-auto mb-1 h-4 w-4" />
            <span className="text-xs font-medium">A Domicilio</span>
          </button>
        </div>

        {/* Delivery details */}
        {saleType === 'delivery' && (
          <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/10 p-3">
            <Button
              onClick={() => setShowDeliveryDetailsModal(true)}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Truck className="mr-2 h-4 w-4" />
              {deliveryDetails.recipientName
                ? 'Editar Datos de Entrega'
                : 'Agregar Datos de Entrega'}
            </Button>
            {deliveryDetails.recipientName && (
              <div className="space-y-1 rounded bg-white p-2 text-xs">
                <p>
                  <strong>Destinatario:</strong> {deliveryDetails.recipientName}
                </p>
                <p>
                  <strong>Teléfono:</strong> {deliveryDetails.customerPhone}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Customer selection */}
        <div className="flex gap-2">
          <button
            onClick={() => setCustomerType('general')}
            className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${customerType === 'general'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-slate-200'
              }`}
          >
            <Users className="mr-1 inline h-3 w-3" />
            Público General
          </button>
          <button
            onClick={() => setCustomerType('registered')}
            className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${customerType === 'registered'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-slate-200'
              }`}
          >
            <User className="mr-1 inline h-3 w-3" />
            Cliente Registrado
          </button>
        </div>

        {customerType === 'registered' && (
          <>
            <div className="flex gap-2">
              <div className="flex-1">
                <CatalogSelect
                  catalog="customers"
                  placeholder="Seleccionar cliente"
                  value={selectedCustomer || undefined}
                  onChange={(val: any) => setSelectedCustomer(val ? Number(val) : 0)}
                  className="h-8 text-sm"
                  itemLabelFormatter={(item: any) => item.phone ? `${item.name} - ${item.phone}` : item.name}
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewCustomerModal(true)}
                className="h-8"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Customer Points Info */}
            {selectedCustomer && (
              <div className="space-y-2 rounded-lg border border-purple-200 bg-purple-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-purple-900">
                    Puntos Disponibles:
                  </span>
                  <span className="text-sm font-bold text-purple-700">
                    {customerPoints.toLocaleString()} pts
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary">
                      Puntos a Ganar:
                    </span>
                    <span className="text-sm font-bold text-primary">
                      +{pointsEarned} pts
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-primary italic">
                    1 punto = ${POINT_VALUE.toFixed(2)}
                  </p>
                </div>

                {/* Use Points */}
                {customerPoints > 0 && (
                  <div className="space-y-2 border-t border-purple-200 pt-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="use-points"
                        checked={usePoints}
                        onCheckedChange={(checked) => {
                          setUsePoints(checked as boolean)
                          if (!checked) setPointsToUse(0)
                        }}
                      />
                      <label
                        htmlFor="use-points"
                        className="cursor-pointer text-xs font-medium text-purple-900"
                      >
                        Usar puntos en esta compra
                      </label>
                    </div>

                    {usePoints && (
                      <div className="space-y-1">
                        <label className="text-xs text-purple-700">
                          Puntos a usar:
                        </label>
                        <Input
                          type="number"
                          min={0}
                          max={Math.min(customerPoints, totalBeforePoints)}
                          value={pointsToUse}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0
                            setPointsToUse(
                              Math.min(
                                value,
                                customerPoints,
                                totalBeforePoints,
                              ),
                            )
                          }}
                          className="h-8 text-sm"
                          placeholder="0"
                        />
                        <p className="text-xs text-purple-600">
                          Máximo:{' '}
                          {Math.min(customerPoints, totalBeforePoints)} pts ($
                          {Math.min(
                            customerPoints,
                            totalBeforePoints,
                          ).toFixed(2)}
                          )
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Cart Items */}
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {cart.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-slate-500">El carrito está vacío</p>
          </div>
        ) : (
          <>
            {cart.some((item) => item.selected) && (
              <Button
                onClick={deleteSelectedItems}
                variant="destructive"
                size="sm"
                className="bg-primary hover:bg-primary/80 w-full"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar Seleccionados
              </Button>
            )}

            {cart.map((item: any) => (
              <div key={item.id} className="relative rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex gap-3">
                  <div className="pt-1">
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                      className="border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                  </div>

                  {item.image ? (
                    <div className="h-14 w-14 overflow-hidden rounded-md border border-primary/20 bg-primary/10 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-14 w-14 flex items-center justify-center rounded-md border border-primary/20 bg-primary/10 flex-shrink-0 text-primary/50">
                      <ImageOff className="h-6 w-6" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-tight">
                        {item.name}
                      </h4>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0 -mt-1 -mr-1 p-1"
                        title="Eliminar del carrito"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                      <span className="font-medium text-slate-500 shrink-0">
                        {item.sku || item.barcode || item.code || 'N/A'}
                      </span>
                      {item.category && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-400 truncate max-w-[120px]">
                            {item.category}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="mt-2.5 flex items-end justify-between">
                      <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg p-0.5 border border-slate-200">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold text-slate-700">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      <div className="text-right">
                        <p className="text-[11px] text-slate-400 mb-0.5 font-medium">
                          ${parseFloat(String(item.amounts?.regular_unit_total || item.price || '0').replace(/[^0-9.-]+/g, "")).toFixed(2)} c/u
                        </p>
                        <p className="text-[15px] font-bold text-primary leading-none">
                          ${parseFloat(String(item.total || item.amounts?.total || (item.price * item.quantity) || '0').replace(/[^0-9.-]+/g, "")).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Totals */}
      {cart.length > 0 && (
        <div className="space-y-3 border-t border-slate-200 p-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Subtotal:</span>
            <span className="font-medium text-slate-900">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">IVA (16%):</span>
            <span className="font-medium text-slate-900">
              ${tax.toFixed(2)}
            </span>
          </div>
          {saleType === 'delivery' && deliveryCost > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Costo de Envío:</span>
              <span className="font-medium text-slate-900">
                ${deliveryCost.toFixed(2)}
              </span>
            </div>
          )}
          {pointsDiscount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-purple-600">Descuento por Puntos:</span>
              <span className="font-medium text-purple-600">
                -${pointsDiscount.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between border-t border-slate-200 pt-3">
            <span className="font-semibold text-slate-900">Total:</span>
            <span className="text-xl font-semibold text-slate-900">
              ${total.toFixed(2)}
            </span>
          </div>
          {customerType === 'registered' && selectedCustomer && (
            <div className="mt-2 rounded-lg border border-primary/20 bg-primary/10 p-2">
              <p className="text-xs text-primary">
                ✓ Cliente ganará{' '}
                <span className="font-bold">{pointsEarned} puntos</span> con
                esta compra
              </p>
            </div>
          )}

          {regulatory?.requires_prescription && (
            <div className={`mt-2 flex items-center justify-between rounded-lg border p-3 ${regulatory.is_ready_for_checkout ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
              <div className="flex items-center gap-2">
                <FileCheck2 className={`h-4 w-4 ${regulatory.is_ready_for_checkout ? 'text-green-700' : 'text-orange-700'}`} />
                <div>
                  <p className={`text-sm font-medium ${regulatory.is_ready_for_checkout ? 'text-green-900' : 'text-orange-900'}`}>
                    {regulatory.is_ready_for_checkout
                      ? `${regulatory.prescriptions_count} ${regulatory.prescriptions_count === 1 ? 'receta capturada' : 'recetas capturadas'}`
                      : 'Recetas pendientes'}
                  </p>
                  <p className="text-xs text-slate-600">{regulatory.covered_items_count} de {regulatory.required_items_count} productos cubiertos</p>
                </div>
              </div>
              {Boolean(prescriptions?.length) && onEditPrescription && (
                <Button type="button" size="sm" variant="ghost" onClick={onEditPrescription}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>
              )}
            </div>
          )}

          <Button
            id="process-payment-button"
            onClick={onProcessPayment}
            className="w-full"
            size="lg"
            disabled={isValidatingCart}
          >
            {isValidatingCart ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <DollarSign className="mr-2 h-5 w-5" />
            )}
            {isValidatingCart ? 'Validando carrito...' : 'Procesar Pago'}
          </Button>
        </div>
      )}
    </div>
  )
}
